/**
 * Format a number with commas and specified decimal places
 * @param {number} value - The number to format
 * @param {number} decimals - Number of decimal places (default: 2)
 * @returns {string} Formatted number
 */
export const formatNumber = (value, decimals = 2) => {
  if (value === null || value === undefined || isNaN(value)) {
    return '-';
  }
  return Number(value).toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
};

/**
 * Format a number as currency (USD)
 * @param {number} value - The number to format as currency
 * @returns {string} Formatted currency
 */
export const formatCurrency = (value) => {
  if (value === null || value === undefined || isNaN(value)) {
    return '-';
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(value);
};

/**
 * Format a number as percentage
 * @param {number} value - The number to format as percentage (0-1)
 * @param {number} decimals - Number of decimal places (default: 1)
 * @returns {string} Formatted percentage
 */
export const formatPercent = (value, decimals = 1) => {
  if (value === null || value === undefined || isNaN(value)) {
    return '-';
  }
  return new Intl.NumberFormat('en-US', {
    style: 'percent',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
};

/**
 * Format an integer with commas
 * @param {number} value - The integer to format
 * @returns {string} Formatted integer
 */
export const formatInteger = (value) => {
  if (value === null || value === undefined || isNaN(value)) {
    return '-';
  }
  return Number(value).toLocaleString(undefined, {
    maximumFractionDigits: 0,
  });
};

/**
 * Format a date value
 * @param {string|Date} date - The date to format
 * @returns {string} Formatted date
 */
export const formatDate = (date) => {
  if (!date) return '-';
  
  const d = new Date(date);
  if (isNaN(d.getTime())) return '-';
  
  return d.toLocaleDateString();
};

/**
 * Get cell formatter function based on format type
 * @param {string} formatType - The type of formatter to use
 * @returns {Function} Formatter function that takes a value and returns formatted string
 */
export const getCellFormatter = (formatType) => {
  switch (formatType) {
    case 'number':
      return (value) => formatNumber(value);
    case 'currency':
      return (value) => formatCurrency(value);
    case 'percent':
      return (value) => formatPercent(value);
    case 'integer':
      return (value) => formatInteger(value);
    case 'date':
      return (value) => formatDate(value);
    case 'numberSigFig':
      return (value) => formatNumberSigFig(value);
    case 'currencySigFig':
      return (value) => formatCurrencySigFig(value);
    default:
      return (value) => (value !== null && value !== undefined ? String(value) : '-');
  }
};

/**
 * Format a number to 4 significant figures
 * @param {number | null | undefined} value - The number to format
 * @param {boolean} includeCommas - Whether to include commas (default: true)
 * @returns {string} Formatted number with 4 significant figures
 */
export const formatNumberSigFig = (value, includeCommas = true) => {
  if (value === null || value === undefined || isNaN(value)) {
    return '-';
  }
  
  // Special handling for zero
  if (value === 0) return '0';
  
  // Calculate magnitude and significant figures
  const sigFigs = 4;
  const magnitude = Math.floor(Math.log10(Math.abs(value))) + 1;
  const scale = Math.pow(10, magnitude - sigFigs);
  const rounded = Math.round(value / scale) * scale;
  
  // Determine decimal places needed for the significant digits
  const decimalsNeeded = Math.max(0, sigFigs - magnitude);
  
  if (includeCommas) {
    return rounded.toLocaleString('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: decimalsNeeded
    });
  } else {
    // Format without commas if requested
    return rounded.toFixed(decimalsNeeded);
  }
};

/**
 * Format a number as currency (USD) with 4 significant figures
 * @param {number | null | undefined} value - The number to format as currency
 * @param {boolean} includeSymbol - Whether to include the $ symbol (default: true)
 * @returns {string} Formatted currency with 4 significant figures
 */
export const formatCurrencySigFig = (value, includeSymbol = true) => {
  if (value === null || value === undefined || isNaN(value)) {
    return '-';
  }
  
  // Special handling for zero
  if (value === 0) return includeSymbol ? '$0' : '0';
  
  // Calculate magnitude and significant figures
  const sigFigs = 4;
  const magnitude = Math.floor(Math.log10(Math.abs(value))) + 1;
  const scale = Math.pow(10, magnitude - sigFigs);
  const rounded = Math.round(value / scale) * scale;
  
  // Format with commas and proper decimal places
  let formattedValue;
  
  if (Math.abs(rounded) >= 1000000) {
    // For values ≥ 1,000,000, use 2 decimal places max
    formattedValue = rounded.toLocaleString('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    });
  } else if (Math.abs(rounded) >= 1000) {
    // For values ≥ 1,000 but < 1,000,000, use 2 decimal places max
    formattedValue = rounded.toLocaleString('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    });
  } else {
    // For smaller values, use up to 4 decimal places if needed for sig figs
    const decimalsNeeded = Math.max(0, sigFigs - magnitude);
    formattedValue = rounded.toLocaleString('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: decimalsNeeded
    });
  }
  
  return includeSymbol ? `$${formattedValue}` : formattedValue;
};